import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Mauguio' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Mauguio',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Mauguio' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Mauguio. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous à Carnon et sur le littoral de Mauguio ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons à Mauguio centre comme à Carnon-Plage. Les résidences proches de la mer nécessitent des pièces anticorrosion que nous utilisons systématiquement. Délai 24-48h, 7j/7.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quels services proposez-vous à Mauguio ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Toute la gamme dépannage menuiserie : baies coulissantes (roulettes, rail, réglage), volets roulants (moteur, sangle, motorisation), vitrerie (vitre cassée, double vitrage), fenêtres (crémone, joint, oscillo-battant) et stores (banne, BSO, motorisation).',
      },
    },
  ],
};

export default function MauguioPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Mauguio | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Mauguio et Carnon ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-mauguio/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Mauguio"
        subtitle="De Mauguio centre à Carnon-Plage, nos techniciens réparent vos baies coulissantes, volets roulants et vitrages. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie en panne à Mauguio ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Mauguio, Carnon et alentours, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Mauguio-Carnon conjugue habitat urbain et résidences littorales. Nous intervenons pour toute panne : baie qui coince, volet bloqué, vitre brisée. Pièces anticorrosion pour le secteur de Carnon. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/mauguio-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Mauguio"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : roulettes et galets à remplacer</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              La chaleur estivale et le sel (côté Carnon) accélèrent l'usure des <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes de baie</Link>. Symptômes : la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link>, frotte ou déraille. Ne tardez pas à nous appeler avant que le rail ne s'abîme.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/serrure-cremone-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">serrure</Link> : nous couvrons tout. <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Remplacement</Link> si nécessaire.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : diagnostic et réparation rapide</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Volet bloqué</Link> en position fermée ou <Link to="/volet-roulant-ne-remonte-plus-herault/" className="text-accent-500 font-semibold hover:underline">qui ne remonte plus</Link> : nous diagnostiquons la panne (moteur, condensateur, lame, sangle) en quelques minutes.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Changement de moteur</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>. Tous types : électriques, radio, solaires, manuels.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/mauguio-volet-02.jpg"
              alt="Réparation de volet roulant à Mauguio"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/mauguio-vitrage-03.jpg"
              alt="Remplacement de vitrage à Mauguio"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et réparation fenêtre à Mauguio</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier en urgence</Link> pour vitre brisée, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> sur mesure, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link> : notre équipe intervient rapidement.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Mauguio sous 24 à 48h</h2>
        <p>
          Nous couvrons Mauguio centre, Carnon-Plage et les zones résidentielles. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-lunel/" className="text-accent-500 font-semibold hover:underline">Lunel</Link>, <Link to="/depannage-menuiserie-la-grande-motte/" className="text-accent-500 font-semibold hover:underline">La Grande-Motte</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}